// Mint a top-level `const <var> = s("<sample>")` statement for a
// user-authored sample (local forge output or downloaded community
// sample). The sample's name on disk is also used as its var name —
// a separate sanitization layer guards against names that aren't
// legal JS identifiers (leading digit, dash, …).
//
// Variable-name collision avoidance follows the same pattern the
// curated Vault uses: callers pass in a Set of already-taken const
// names; the helper appends "2", "3", … until unused.

import { patternFromString } from '../mini';
import {
  newBlockId,
  type Effect,
  type Statement,
  type VoiceBlock,
} from '../types';

export type MakeLocalSampleStatementInput = {
  /** The sample's registered name (matches `s("<name>")`). */
  sampleName: string;
  /** The set of const names already in the song. The minted
   *  statement gets a unique name disjoint from this set. */
  takenNames: Set<string>;
  /** Optional initial gain. Defaults to 0.8 to match the Vault. */
  gain?: number;
};

export function makeLocalSampleStatement({
  sampleName,
  takenNames,
  gain = 0.8,
}: MakeLocalSampleStatementInput): Statement {
  const varBase = toVarBase(sampleName);
  const varName = uniqueName(varBase, takenNames);

  const gainEffect: Effect = {
    id: newBlockId(),
    kind: 'gain',
    args: [{ kind: 'numeric', value: { kind: 'num', value: gain } }],
  };

  const voice: VoiceBlock = {
    id: newBlockId(),
    kind: 'voice.s',
    pattern: patternFromString(sampleName),
    effects: [gainEffect],
  };

  return {
    id: newBlockId(),
    kind: 'const',
    name: varName,
    expr: voice,
  };
}

// Sample names accept `[a-z0-9_]` from sanitizeName, plus '-' from
// the cloud `samples.name` check. Var names additionally can't lead
// with a digit. Map both into a safe `[a-z][a-z0-9_]*` shape.
function toVarBase(sampleName: string): string {
  const ascii = sampleName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  if (ascii.length === 0) return 's';
  if (/^[0-9]/.test(ascii)) return 's_' + ascii;
  return ascii;
}

function uniqueName(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}${i}`)) i++;
  return `${base}${i}`;
}
